import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  Input,
  Link,
  Select,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Textarea,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { getCustomerCockpitProfileApi } from 'api/customerCockpitProfileApi';
import {
  deleteFinancialSnapshotVersionApi,
  getFinancialSnapshotVersionApi,
  listFinancialSnapshotVersionsApi,
  saveFinancialSnapshotVersionApi,
} from 'api/financialSnapshotVersionsApi';
import { reExtractFinancialSnapshotApi } from 'api/financialSnapshotExtractApi';
import Card from 'components/card/Card';
import CockpitAssistChat from 'components/CockpitAssistChat';
import { HelperTooltip } from 'components/Tooltip';
import { CockpitCustomerContext } from 'context/CockpitCustomerContext';
import moment from 'moment';
import { useContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import AssistResultsPanel from '../AssistResultsPanel';
import CohortSheetTable from '../CohortSheetTable';
import CopyRoasVectorButton from '../CopyRoasVectorButton';
import {
  mergeComputedSnapshotFields,
  resolveRoasVectorFromCohortSheet,
} from '../hydrateModelFromFinancialSnapshot';
import { useQuickCashflowAssist } from '../useQuickCashflowAssist';
import {
  applyDraftFieldEdit,
  cloneSnapshotMap,
  normalizeSnapshotNarrativeEntry,
  snapshotVersionLabel,
} from './financialSnapshotDraft';
import { resolveSnapshotSaveOverwriteTarget } from './resolveSnapshotSaveOverwriteTarget';
import {
  clearSnapshotCohort,
  clearSnapshotFinance,
  snapshotHasCohortData,
  snapshotHasFinanceData,
} from './clearSnapshotSide';
import {
  DriveExtractStatusBanner,
  isActiveDriveExtractStatus,
} from './DriveExtractStatusBanner';
import ExtractSourceOptions, { DATAROOM_INGEST_EMAIL } from './ExtractSourceOptions';
import {
  FINANCIAL_SNAPSHOT_FIELD_TOOLTIPS,
  FINANCIAL_SNAPSHOT_READ_ONLY_CALCULATED_KEYS,
  FINANCIAL_SNAPSHOT_SOURCE_FIELD_KEYS,
  extractInfoBullets,
  groupSnapshotMetricKeys,
  snapshotFieldLabel,
} from './financialSnapshotFieldOrder';
import '../quickCashflow.scss';

// Fields whose value is a compound object (not a single number) rather than a scalar — no
// mockup precedent for editing these inline as a single text value, so their Edit mode only
// exposes the Comment field.
const COMPOUND_VALUE_FIELDS = ['safe', 'cohortData'];

const formatSnapshotValue = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'number') return value.toLocaleString();
  return String(value);
};

// Every field is persisted as { value, comment, lastUpdated, source, sourceDocument,
// sourceDocumentUrl } (see usecase.normalizeMailIntakeFinancialSnapshotField on the
// backend). Snapshots saved before that normalization shipped only have the field's raw
// ChatGPT shape (e.g. { value, reason } or, for safe, { hasSafe, amount, reason } directly)
// — detected here by the absence of "lastUpdated" — and are read as un-stamped, un-sourced
// rows rather than backfilled, matching how cohortData's optional presence already worked.
function normalizeSnapshotEntry(key, entry) {
  if (!entry || typeof entry !== 'object') {
    return {
      rawValue: entry,
      comment: '',
      lastUpdated: null,
      source: '',
      sourceDocument: '',
      sourceDocumentUrl: '',
    };
  }

  if ('lastUpdated' in entry) {
    return {
      rawValue: entry.value,
      comment: entry.comment || '',
      lastUpdated: entry.lastUpdated || null,
      source: entry.source || '',
      sourceDocument: entry.sourceDocument || '',
      sourceDocumentUrl: entry.sourceDocumentUrl || '',
    };
  }

  let rawValue;
  if (key === 'safe') {
    rawValue = { hasSafe: entry.hasSafe, amount: entry.amount };
  } else if (key === 'cohortData') {
    rawValue = { applied: entry.applied, metric: entry.metric, rows: entry.rows };
  } else {
    rawValue = entry.value;
  }
  return {
    rawValue,
    comment: entry.reason || '',
    lastUpdated: null,
    source: '',
    sourceDocument: '',
    sourceDocumentUrl: '',
  };
}

function formatRawValue(key, rawValue) {
  if (key === 'safe' && rawValue && typeof rawValue === 'object') {
    const hasSafeText =
      rawValue.hasSafe === true ? 'Yes' : rawValue.hasSafe === false ? 'No' : '—';
    return rawValue.amount != null
      ? `${hasSafeText} — ${formatSnapshotValue(rawValue.amount)}`
      : hasSafeText;
  }
  if (key === 'qcfGpm') {
    const n = Number(rawValue);
    if (!Number.isFinite(n)) return '—';
    return n.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }
  return formatSnapshotValue(rawValue);
}

// Parses an edited Value input back to a number when possible (commas stripped), so numeric
// fields keep rendering with toLocaleString() after a manual edit rather than turning into
// a formatted-looking string that no longer matches.
function parseEditedValue(raw) {
  const trimmed = String(raw).trim();
  const numeric = Number(trimmed.replace(/,/g, ''));
  return trimmed !== '' && !Number.isNaN(numeric) ? numeric : trimmed;
}

function SourceBadge({ source }) {
  if (!source) return null;
  return (
    <Badge colorScheme={source === 'Manual' ? 'purple' : 'gray'} fontSize="xs">
      {source}
    </Badge>
  );
}

function DocumentLink({ sourceDocument, sourceDocumentUrl }) {
  if (!sourceDocument) return <Text color="secondaryGray.600">—</Text>;
  if (!sourceDocumentUrl) return <Text fontSize="sm">{sourceDocument}</Text>;
  return (
    <Link href={sourceDocumentUrl} isExternal color="brand.500" fontSize="sm">
      {sourceDocument}
    </Link>
  );
}

function CommentCallout({ comment }) {
  if (!comment) return null;
  return (
    <Box
      mt={2}
      px={3}
      py={2}
      bg="secondaryGray.100"
      borderLeftWidth="3px"
      borderLeftColor="brand.300"
      borderRadius="md"
    >
      <Text fontSize="xs" fontWeight={700} textTransform="uppercase" color="secondaryGray.600">
        Comment
      </Text>
      <Text fontSize="sm">{comment}</Text>
    </Box>
  );
}

function FieldSectionHeading({ children }) {
  return (
    <Text
      mt={6}
      mb={1}
      fontSize="xs"
      fontWeight={700}
      textTransform="uppercase"
      color="secondaryGray.600"
    >
      {children}
    </Text>
  );
}

function FinancialSnapshotCalculatedFieldRow({ fieldKey, entry }) {
  const { rawValue, comment } = normalizeSnapshotEntry(fieldKey, entry);

  return (
    <Box borderTopWidth="1px" borderColor="secondaryGray.200" py={3}>
      <Flex align="center" gap={4} wrap="wrap">
        <Flex align="center" gap={1} flex="2" minW="180px">
          <Text fontWeight={500}>{snapshotFieldLabel(fieldKey)}</Text>
          {FINANCIAL_SNAPSHOT_FIELD_TOOLTIPS[fieldKey] && (
            <HelperTooltip
              content={FINANCIAL_SNAPSHOT_FIELD_TOOLTIPS[fieldKey]}
              placement="top"
            />
          )}
        </Flex>
        <Text flex="1" minW="100px" fontWeight={700} fontVariantNumeric="tabular-nums">
          {formatRawValue(fieldKey, rawValue)}
        </Text>
        <Text flex="1" minW="120px" fontSize="sm" color="secondaryGray.600">
          —
        </Text>
        <Box flex="1" minW="120px">
          <Badge colorScheme="blue" fontSize="xs">
            Calculated
          </Badge>
        </Box>
        <Box flex="1" minW="140px">
          <Text color="secondaryGray.600">—</Text>
        </Box>
        <Box flexShrink={0} w="70px" />
      </Flex>
      <CommentCallout comment={comment} />
    </Box>
  );
}

function FinancialSnapshotFieldRow({ fieldKey, entry, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draftValue, setDraftValue] = useState('');
  const [draftComment, setDraftComment] = useState('');
  const [saving, setSaving] = useState(false);

  const { rawValue, comment, lastUpdated, source, sourceDocument, sourceDocumentUrl } =
    normalizeSnapshotEntry(fieldKey, entry);
  const isCompoundValue = COMPOUND_VALUE_FIELDS.includes(fieldKey);

  const startEdit = () => {
    setDraftValue(isCompoundValue ? '' : String(rawValue ?? ''));
    setDraftComment(comment);
    setEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(
        fieldKey,
        isCompoundValue ? rawValue : parseEditedValue(draftValue),
        draftComment
      );
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box borderTopWidth="1px" borderColor="secondaryGray.200" py={3}>
      <Flex align="center" gap={4} wrap="wrap">
        <Flex align="center" gap={1} flex="2" minW="180px">
          <Text fontWeight={500}>{snapshotFieldLabel(fieldKey)}</Text>
          {FINANCIAL_SNAPSHOT_FIELD_TOOLTIPS[fieldKey] && (
            <HelperTooltip
              content={FINANCIAL_SNAPSHOT_FIELD_TOOLTIPS[fieldKey]}
              placement="top"
            />
          )}
        </Flex>
        <Text flex="1" minW="100px" fontWeight={700} fontVariantNumeric="tabular-nums">
          {formatRawValue(fieldKey, rawValue)}
        </Text>
        <Text flex="1" minW="120px" fontSize="sm" color="secondaryGray.600">
          {lastUpdated ? moment(lastUpdated).format('MMM D, YYYY h:mm A') : '—'}
        </Text>
        <Box flex="1" minW="120px">
          <SourceBadge source={source} />
        </Box>
        <Box flex="1" minW="140px">
          <DocumentLink sourceDocument={sourceDocument} sourceDocumentUrl={sourceDocumentUrl} />
        </Box>
        <Box flexShrink={0}>
          {!editing && (
            <Button size="sm" variant="outline" onClick={startEdit}>
              Edit
            </Button>
          )}
        </Box>
      </Flex>

      {!editing && <CommentCallout comment={comment} />}

      {editing && (
        <Box mt={3} p={4} bg="secondaryGray.100" borderRadius="lg">
          <Flex gap={4} align="flex-start" wrap="wrap">
            {!isCompoundValue && (
              <Box flex="1" minW="200px">
                <Text fontSize="xs" color="secondaryGray.600" mb={1}>
                  Value
                </Text>
                <Input
                  value={draftValue}
                  onChange={(e) => setDraftValue(e.target.value)}
                  bg="white"
                />
              </Box>
            )}
            <Box flex="2" minW="240px">
              <Text fontSize="xs" color="secondaryGray.600" mb={1}>
                Comment
              </Text>
              <Textarea
                value={draftComment}
                onChange={(e) => setDraftComment(e.target.value)}
                placeholder="Why is this value being changed?"
                rows={2}
                bg="white"
              />
            </Box>
          </Flex>
          <Flex justify="space-between" align="center" mt={3}>
            <Text fontSize="xs" color="secondaryGray.600">
              Applies to this draft only. Press Save in the snapshot header to persist.
            </Text>
            <Flex gap={2}>
              <Button size="sm" variant="outline" onClick={() => setEditing(false)}>
                Cancel
              </Button>
              <Button size="sm" variant="brand" onClick={handleSave} isLoading={saving}>
                Save
              </Button>
            </Flex>
          </Flex>
        </Box>
      )}
    </Box>
  );
}

// Processed QCF cohort triangle (qcfCohortSheet.value shape matches Create cohortSheet).
function CumulativeRoasVectorSection({ cohortSheet }) {
  const vector = resolveRoasVectorFromCohortSheet(cohortSheet);
  if (!vector.length) {
    return null;
  }
  const finalRoas = vector[vector.length - 1] || 0;
  return (
    <Box
      mb={4}
      border="1px solid"
      borderColor="purple.100"
      borderRadius="lg"
      p={4}
      bg="purple.50"
    >
      <Flex justify="space-between" mb={3} flexWrap="wrap" gap={2} align="center">
        <Text fontWeight="semibold">Cumulative ROAS vector</Text>
        <CopyRoasVectorButton vectorPct={vector} />
      </Flex>
      <Flex flexWrap="wrap" gap={2} mb={3} bg="white" p={3} borderRadius="lg">
        {vector.map((value, i) => (
          <Box key={`roas-${i}`} className="roas-cell" textAlign="center">
            <Text className="annotation" mb={1}>
              M{i}
            </Text>
            <Input
              type="number"
              size="sm"
              textAlign="center"
              value={value}
              isReadOnly
            />
          </Box>
        ))}
      </Flex>
      <Text fontSize="sm" className="annotation">
        Final cumulative ROAS:{' '}
        <span className="accent-text" style={{ fontWeight: 600 }}>
          {finalRoas.toFixed(0)}%
        </span>
        {' · '}
        Create loads this vector from the processed cohort triangle.
      </Text>
    </Box>
  );
}

function ProcessedCohortSection({ entry, emptyMessage }) {
  const { rawValue, comment, lastUpdated, source, sourceDocument, sourceDocumentUrl } =
    normalizeSnapshotEntry('qcfCohortSheet', entry);

  if (!rawValue?.cohorts?.length) {
    return emptyMessage ? (
      <Text color="secondaryGray.600">{emptyMessage}</Text>
    ) : null;
  }

  return (
    <Box mt={2}>
      <Flex align="center" gap={4} wrap="wrap" mb={3}>
        <Text fontWeight={500}>Processed cohort (ROAS triangle)</Text>
        <Text fontSize="sm" color="secondaryGray.600">
          {lastUpdated ? `Last updated ${moment(lastUpdated).format('MMM D, YYYY h:mm A')}` : ''}
        </Text>
        <SourceBadge source={source} />
        <DocumentLink sourceDocument={sourceDocument} sourceDocumentUrl={sourceDocumentUrl} />
      </Flex>
      {comment ? <CommentCallout comment={comment} /> : null}
      <CumulativeRoasVectorSection cohortSheet={rawValue} />
      <CohortSheetTable cohortSheet={rawValue} expanded />
    </Box>
  );
}

function ExtractInfoBullets({ snapshot }) {
  const bullets = extractInfoBullets(snapshot);
  if (bullets.length === 0) {
    return null;
  }
  return (
    <Box mb={4} fontSize="sm" color="secondaryGray.700">
      <Text fontWeight={600} mb={2}>
        Extract info
      </Text>
      {bullets.map(({ key, label, value }) => (
        <Text key={key} mb={1}>
          •{' '}
          <Text as="span" fontWeight={500}>
            {label}:
          </Text>{' '}
          {key === 'qcfExtractedAt'
            ? moment(value).format('MMM D, YYYY h:mm A')
            : value}
        </Text>
      ))}
    </Box>
  );
}

function SelectedFilesSection({ files }) {
  const list = Array.isArray(files) ? files : files?.value;
  if (!Array.isArray(list) || list.length === 0) {
    return null;
  }
  return (
    <Box mt={6}>
      <Text fontWeight={500} mb={2}>
        Selected files (last extract)
      </Text>
      {list.map((file) => (
        <Text key={file.driveFileId || file.name} fontSize="sm">
          • {file.name}
          {file.role ? ` (${file.role})` : ''}
        </Text>
      ))}
    </Box>
  );
}

// Renders the current extract run's document summary or explanation.
function SnapshotHistorySection({ title, tooltip, entry, entries }) {
  const narrative = normalizeSnapshotNarrativeEntry(entry ?? entries);
  if (!narrative) return null;

  return (
    <Box mt={6}>
      <Flex align="center" gap={1} mb={2}>
        <Text fontWeight={500}>{title}</Text>
        {tooltip && <HelperTooltip content={tooltip} placement="top" />}
      </Flex>
      <Box
        px={3}
        py={2}
        bg="secondaryGray.100"
        borderLeftWidth="3px"
        borderLeftColor="brand.300"
        borderRadius="md"
      >
        <Text fontSize="sm" whiteSpace="pre-wrap">
          {narrative.value}
        </Text>
        {(narrative.lastUpdated || narrative.from) && (
          <Text fontSize="xs" color="secondaryGray.600" mt={1}>
            {narrative.lastUpdated ? moment(narrative.lastUpdated).format('MMM D, YYYY h:mm A') : ''}
            {narrative.from ? `${narrative.lastUpdated ? ' — ' : ''}${narrative.from}` : ''}
          </Text>
        )}
      </Box>
    </Box>
  );
}

function FinancialSnapshotData() {
  const { customerId } = useParams();
  const { customer } = useContext(CockpitCustomerContext);
  const companyLabel = customer?.companyName || 'Customer';
  const toast = useToast();

  const [profile, setProfile] = useState(null);
  const [savedVersions, setSavedVersions] = useState([]);
  const [loadVersionId, setLoadVersionId] = useState('');
  const [snapshotName, setSnapshotName] = useState('');
  const [draftSnapshot, setDraftSnapshot] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [savingSnapshot, setSavingSnapshot] = useState(false);
  const [deletingSnapshot, setDeletingSnapshot] = useState(false);
  const [saveOverwriteTarget, setSaveOverwriteTarget] = useState('');
  const [clearingSide, setClearingSide] = useState('');
  const [tabIndex, setTabIndex] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [reExtracting, setReExtracting] = useState(false);
  const watchingDriveExtractRef = useRef(false);
  const saveCancelRef = useRef(null);
  const {
    isOpen: isSaveConfirmOpen,
    onOpen: openSaveConfirm,
    onClose: closeSaveConfirm,
  } = useDisclosure();

  const loadVersionById = useCallback(
    async (versionId, { force = false } = {}) => {
      if (!versionId) {
        return;
      }
      if (isDirty && !force) {
        const discard = window.confirm('Discard unsaved changes to this snapshot?');
        if (!discard) {
          return;
        }
      }
      const version = await getFinancialSnapshotVersionApi(versionId);
      if (!version) {
        return;
      }
      setLoadVersionId(version.id);
      setSnapshotName(version.name);
      setDraftSnapshot(cloneSnapshotMap(version.snapshot));
      setIsDirty(false);
    },
    [isDirty]
  );

  const reloadVersions = useCallback(
    async (selectLatest = false) => {
      const rows = await listFinancialSnapshotVersionsApi(customerId);
      setSavedVersions(rows || []);
      if (selectLatest && rows?.length) {
        await loadVersionById(rows[0].id, { force: true });
      } else if (!rows?.length) {
        setLoadVersionId('');
        setSnapshotName('');
        setDraftSnapshot(null);
        setIsDirty(false);
      }
      return rows || [];
    },
    [customerId, loadVersionById]
  );

  const loadProfileAndVersions = useCallback(async () => {
    setLoading(true);
    try {
      const profileData = await getCustomerCockpitProfileApi(customerId);
      setProfile(profileData);
      const rows = await reloadVersions(false);
      if (rows.length > 0) {
        await loadVersionById(rows[0].id, { force: true });
      }
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to load financial snapshot',
        status: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [customerId, loadVersionById, reloadVersions, toast]);

  const pollProfileQuiet = useCallback(async () => {
    try {
      const data = await getCustomerCockpitProfileApi(customerId);
      setProfile(data);
      return data;
    } catch {
      return null;
    }
  }, [customerId]);

  const assist = useQuickCashflowAssist({
    customerId,
    customerName: companyLabel,
    toast,
    onExtractPersisted: async () => {
      await pollProfileQuiet();
      await reloadVersions(true);
    },
  });

  useEffect(() => {
    if (customerId) {
      loadProfileAndVersions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const driveExtractStatus = profile?.financialSnapshot?.qcfDriveExtractStatus;
  const isDriveExtractActive = isActiveDriveExtractStatus(driveExtractStatus?.status);

  useEffect(() => {
    if (isDriveExtractActive) {
      watchingDriveExtractRef.current = true;
    }
  }, [isDriveExtractActive]);

  useEffect(() => {
    if (!customerId || !isDriveExtractActive) {
      return undefined;
    }
    const timer = setInterval(() => {
      pollProfileQuiet();
    }, 3000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, isDriveExtractActive]);

  useEffect(() => {
    const status = driveExtractStatus?.status;
    if (!status || isActiveDriveExtractStatus(status)) {
      return;
    }
    if (!watchingDriveExtractRef.current) {
      return;
    }
    watchingDriveExtractRef.current = false;
    const description =
      driveExtractStatus.error ||
      driveExtractStatus.stageMessage ||
      'Drive folder re-extract finished.';
    if (status === 'complete') {
      toast({ title: 'Re-extract complete', description, status: 'success' });
      pollProfileQuiet().then(() => reloadVersions(true));
    } else if (status === 'skipped') {
      toast({ title: 'Re-extract skipped', description, status: 'warning' });
    } else if (status === 'failed') {
      toast({ title: 'Re-extract failed', description, status: 'error' });
    }
  }, [driveExtractStatus, toast, pollProfileQuiet, reloadVersions]);

  const onSaveField = async (fieldKey, value, comment) => {
    if (FINANCIAL_SNAPSHOT_READ_ONLY_CALCULATED_KEYS.includes(fieldKey)) {
      return;
    }
    setDraftSnapshot((previous) =>
      applyDraftFieldEdit(previous, fieldKey, value, comment, normalizeSnapshotEntry)
    );
    setIsDirty(true);
  };

  const persistSnapshot = async (snapshotToSave, { successTitle, name: nameOverride } = {}) => {
    const name = String(nameOverride || snapshotName).trim();
    if (!name) {
      toast({ title: 'Enter a snapshot name first', status: 'warning' });
      return false;
    }
    if (!snapshotToSave) {
      toast({ title: 'Nothing to save', status: 'warning' });
      return false;
    }
    setSavingSnapshot(true);
    try {
      const saved = await saveFinancialSnapshotVersionApi(customerId, {
        name,
        snapshot: mergeComputedSnapshotFields(snapshotToSave),
        source: 'manual',
      });
      await reloadVersions(false);
      if (saved?.id) {
        setLoadVersionId(saved.id);
        setSnapshotName(saved.name);
        setDraftSnapshot(cloneSnapshotMap(saved.snapshot));
      }
      setIsDirty(false);
      await pollProfileQuiet();
      toast({ title: successTitle || `Saved "${name}"`, status: 'success' });
      return true;
    } catch (err) {
      toast({
        title: 'Save failed',
        description: err?.response?.data?.error || err.message,
        status: 'error',
      });
      return false;
    } finally {
      setSavingSnapshot(false);
    }
  };

  const performSaveSnapshot = async () => persistSnapshot(draftSnapshot);

  const onSaveSnapshot = () => {
    const name = snapshotName.trim();
    if (!name) {
      toast({ title: 'Enter a snapshot name first', status: 'warning' });
      return;
    }
    const overwriteTarget = resolveSnapshotSaveOverwriteTarget({
      loadVersionId,
      snapshotName: name,
      savedVersions,
    });
    if (overwriteTarget) {
      setSaveOverwriteTarget(overwriteTarget);
      openSaveConfirm();
      return;
    }
    performSaveSnapshot();
  };

  const onConfirmSaveSnapshot = async () => {
    closeSaveConfirm();
    await performSaveSnapshot();
  };

  const onClearSnapshotSide = async (side) => {
    if (!draftSnapshot) {
      return;
    }
    const label = snapshotName.trim() || 'this snapshot';
    const confirmed = window.confirm(
      side === 'finance'
        ? `Clear finance data from "${label}"? Cohort data is kept.`
        : `Clear cohort data from "${label}"? Finance data is kept.`
    );
    if (!confirmed) {
      return;
    }
    const next =
      side === 'finance'
        ? clearSnapshotFinance(draftSnapshot)
        : clearSnapshotCohort(draftSnapshot);
    setDraftSnapshot(next);
    setIsDirty(true);
    if (!snapshotName.trim()) {
      toast({
        title: side === 'finance' ? 'Finance data cleared' : 'Cohort data cleared',
        description: 'Press Save to persist.',
        status: 'info',
      });
      return;
    }
    setClearingSide(side);
    try {
      const loadedName = savedVersions.find((version) => version.id === loadVersionId)?.name;
      await persistSnapshot(next, {
        successTitle: side === 'finance' ? 'Finance data cleared' : 'Cohort data cleared',
        name: loadedName || snapshotName,
      });
    } finally {
      setClearingSide('');
    }
  };

  const onDeleteSnapshot = async () => {
    if (!loadVersionId) {
      toast({ title: 'Load a snapshot first', status: 'warning' });
      return;
    }
    const loaded = savedVersions.find((version) => version.id === loadVersionId);
    const label = loaded?.name || 'this snapshot';
    if (!window.confirm(`Delete "${label}"? This cannot be undone.`)) {
      return;
    }
    setDeletingSnapshot(true);
    try {
      await deleteFinancialSnapshotVersionApi(loadVersionId);
      await pollProfileQuiet();
      await reloadVersions(true);
      toast({ title: 'Snapshot deleted', status: 'success' });
    } catch (err) {
      toast({
        title: 'Delete failed',
        description: err?.response?.data?.error || err.message,
        status: 'error',
      });
    } finally {
      setDeletingSnapshot(false);
    }
  };

  const onReExtract = async () => {
    setReExtracting(true);
    watchingDriveExtractRef.current = true;
    try {
      const response = await reExtractFinancialSnapshotApi(customerId, companyLabel);
      if (response?.driveExtractStatus) {
        setProfile((prev) => ({
          ...prev,
          financialSnapshot: {
            ...(prev?.financialSnapshot || {}),
            qcfDriveExtractStatus: response.driveExtractStatus,
          },
        }));
      } else {
        await pollProfileQuiet();
      }
    } catch {
      watchingDriveExtractRef.current = false;
      toast({
        title: 'Error',
        description: 'Failed to start re-extract',
        status: 'error',
      });
    } finally {
      setReExtracting(false);
    }
  };

  const rawSnapshot = draftSnapshot;
  const snapshot = useMemo(() => {
    if (!rawSnapshot) {
      return null;
    }
    return mergeComputedSnapshotFields(rawSnapshot);
  }, [rawSnapshot]);

  if (!customerId) {
    return (
      <Box p={8}>
        <Text>Open this page from a customer record.</Text>
      </Box>
    );
  }

  // Non-Qcf fields (the ad hoc mail-intake pipeline's opex/cashBalance/etc.) are hidden from
  // this list for now — they're still written to and readable from the DB, just not shown
  // here while the Qcf pipeline is the one surfaced to cockpit users.
  const { sourceKeys, extraKeys, calculatedKeys } = groupSnapshotMetricKeys(snapshot);
  const metricKeys = [...sourceKeys, ...extraKeys, ...calculatedKeys];
  const hasFinanceData = snapshotHasFinanceData(draftSnapshot);
  const hasCohortData = snapshotHasCohortData(draftSnapshot);
  const manualOverrideCount = metricKeys.filter(
    (key) =>
      !FINANCIAL_SNAPSHOT_READ_ONLY_CALCULATED_KEYS.includes(key) &&
      normalizeSnapshotEntry(key, snapshot?.[key]).source === 'Manual'
  ).length;

  return (
    <Box className="quick-cashflow-page quick-cashflow-embed" px={8} py={5} maxW="1400px" mx="auto">
      <Box mb={6}>
        <Text fontSize="xs" textTransform="uppercase" color="gray.400">
          First Underwriting
        </Text>
        <Flex justify="space-between" align="flex-start" gap={4} wrap="wrap">
          <Text fontSize="2xl" fontWeight="bold" className="brand">
            {companyLabel} — Data Extractor
          </Text>
          <Button
            as={RouterLink}
            to={`/customers/${customerId}/first-underwriting/create`}
            size="sm"
            variant="outline"
          >
            Open Create (score)
          </Button>
        </Flex>
      </Box>

      <DriveExtractStatusBanner driveExtractStatus={driveExtractStatus} />

      <Card mb={6}>
        <ExtractSourceOptions
          onRunManualExtract={onReExtract}
          reExtracting={reExtracting}
          forceChatOpen={
            assist.assistSession?.extractJob?.status === 'running' ||
            assist.assistSession?.extractJob?.status === 'pending'
          }
          dragActive={dragActive}
          onDragEnter={(e) => {
            e.preventDefault();
            setDragActive(true);
          }}
          onDragOver={(e) => e.preventDefault()}
          onDragLeave={() => setDragActive(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragActive(false);
            const files = Array.from(e.dataTransfer.files || []);
            if (files.length) assist.setDroppedFiles(files);
          }}
        >
          <CockpitAssistChat
            key={assist.assistChatKey}
            onSend={assist.handleAssistSend}
            acceptFiles=".csv,.xlsx,.xls,.tsv,.pdf"
            initialMessages={assist.welcomeMessages}
            quickActions={assist.assistMenu.quickActions}
            showBack={assist.assistMenu.showBack}
            backMessage={assist.assistMenu.backMessage}
            appendMessage={assist.extractChatAppend}
            annotation="Choose Cohort, Accounting, or Marketing — then upload, Drive, or describe in chat."
            placeholder="Choose a shortcut above or type here…"
            droppedFiles={assist.droppedFiles}
            onDroppedFilesConsumed={() => assist.setDroppedFiles([])}
          />
          <AssistResultsPanel
            accountingSnapshot={assist.accountingSnapshot}
            assistSession={assist.assistSession}
            extractJob={assist.assistSession?.extractJob}
            extractUsage={assist.extractUsage}
            cohortApplied={Boolean(assist.cohortSheet?.cohorts?.length)}
            onCancelExtract={assist.handleCancelExtract}
            cancellingExtract={assist.cancellingExtract}
          />
        </ExtractSourceOptions>
      </Card>

      {loading && <Text color="gray.500">Loading…</Text>}

      {!loading && !snapshot && (
        <Card>
          <Text color="secondaryGray.600">
            No financial data yet. Run the extractor, email files to {DATAROOM_INGEST_EMAIL}, or
            open chat.
          </Text>
        </Card>
      )}

      {!loading && snapshot && (
        <Card>
          <Flex justify="space-between" align="flex-start" gap={4} wrap="wrap" mb={4}>
            <Heading variant="sectionHeader" borderColor="secondaryGray.600">
              Financial snapshot
            </Heading>
            <Flex align="center" gap={3} wrap="wrap">
              <Flex align="center" gap={2}>
                <Text fontSize="sm" color="gray.500">
                  Snapshot name:
                </Text>
                <Input
                  w="280px"
                  size="sm"
                  value={snapshotName}
                  onChange={(e) => {
                    setSnapshotName(e.target.value);
                    setIsDirty(true);
                  }}
                  placeholder="e.g. Extract 17 Aug 2026, 12:04:05"
                />
              </Flex>
              <Select
                w="320px"
                size="sm"
                placeholder="Load snapshot…"
                value={loadVersionId}
                onChange={(e) => loadVersionById(e.target.value)}
              >
                {savedVersions.map((version) => (
                  <option key={version.id} value={version.id}>
                    {snapshotVersionLabel(version)}
                  </option>
                ))}
              </Select>
              <Button
                size="sm"
                variant="outline"
                colorScheme="purple"
                onClick={onSaveSnapshot}
                isLoading={savingSnapshot}
              >
                Save
              </Button>
              <Button
                size="sm"
                variant="outline"
                colorScheme="red"
                onClick={onDeleteSnapshot}
                isLoading={deletingSnapshot}
                isDisabled={!loadVersionId}
              >
                Delete
              </Button>
            </Flex>
          </Flex>

          {isDirty ? (
            <Box
              mb={4}
              px={4}
              py={3}
              bg="orange.50"
              borderWidth="1px"
              borderColor="orange.200"
              borderRadius="md"
            >
              <Text fontSize="sm" color="orange.800">
                Changes are not saved. Press Save to overwrite this snapshot or use a new name to
                create another version.
              </Text>
            </Box>
          ) : null}

          <Flex justify="flex-end">
            <Text fontSize="xs" color="secondaryGray.600">
              {manualOverrideCount === 0
                ? 'No manual overrides'
                : `${manualOverrideCount} parameter${
                    manualOverrideCount === 1 ? '' : 's'
                  } manually overridden`}
            </Text>
          </Flex>
          <Tabs index={tabIndex} onChange={setTabIndex} colorScheme="blue" mt={4}>
            <TabList>
              <Tab>Financial Data</Tab>
              <Tab>Cohort</Tab>
            </TabList>
            <TabPanels>
              <TabPanel px={0}>
                <Flex justify="flex-end" mb={3}>
                  <Button
                    size="sm"
                    variant="outline"
                    colorScheme="red"
                    onClick={() => onClearSnapshotSide('finance')}
                    isDisabled={!hasFinanceData}
                    isLoading={clearingSide === 'finance'}
                  >
                    Clear finance
                  </Button>
                </Flex>
                <ExtractInfoBullets snapshot={snapshot} />

                <Flex
                  gap={4}
                  px={1}
                  pt={2}
                  fontSize="xs"
                  fontWeight={700}
                  textTransform="uppercase"
                  color="secondaryGray.600"
                  wrap="wrap"
                >
                  <Text flex="2" minW="180px">
                    Parameter
                  </Text>
                  <Text flex="1" minW="100px">
                    Value
                  </Text>
                  <Text flex="1" minW="120px">
                    Last Updated
                  </Text>
                  <Text flex="1" minW="120px">
                    Source
                  </Text>
                  <Text flex="1" minW="140px">
                    Document
                  </Text>
                  <Box flexShrink={0} w="70px" />
                </Flex>

                <Box fontSize="sm">
                  {FINANCIAL_SNAPSHOT_SOURCE_FIELD_KEYS.map((key) => (
                    <FinancialSnapshotFieldRow
                      key={key}
                      fieldKey={key}
                      entry={snapshot?.[key]}
                      onSave={onSaveField}
                    />
                  ))}
                  {extraKeys.map((key) => (
                    <FinancialSnapshotFieldRow
                      key={key}
                      fieldKey={key}
                      entry={snapshot?.[key]}
                      onSave={onSaveField}
                    />
                  ))}
                  <FieldSectionHeading>Calculated</FieldSectionHeading>
                  {calculatedKeys.map((key) => (
                    <FinancialSnapshotCalculatedFieldRow
                      key={key}
                      fieldKey={key}
                      entry={snapshot?.[key]}
                    />
                  ))}
                </Box>

                <SnapshotHistorySection
                  title="Qcf Document Summary"
                  entry={snapshot.qcfDocumentSummary}
                />
                <SnapshotHistorySection
                  title="Qcf Explanation"
                  entry={snapshot.qcfExplanation}
                />

                <SelectedFilesSection files={snapshot.qcfSelectedFiles} />
              </TabPanel>
              <TabPanel px={0}>
                <Flex justify="flex-end" mb={3}>
                  <Button
                    size="sm"
                    variant="outline"
                    colorScheme="red"
                    onClick={() => onClearSnapshotSide('cohort')}
                    isDisabled={!hasCohortData}
                    isLoading={clearingSide === 'cohort'}
                  >
                    Clear cohort
                  </Button>
                </Flex>
                <ProcessedCohortSection
                  entry={snapshot.qcfCohortSheet}
                  emptyMessage="No processed cohort data yet. Upload a cohort file via ingest or wait for email automation."
                />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Card>
      )}

      <AlertDialog
        isOpen={isSaveConfirmOpen}
        leastDestructiveRef={saveCancelRef}
        onClose={closeSaveConfirm}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Overwrite saved snapshot?
            </AlertDialogHeader>
            <AlertDialogBody>
              Overwrite &ldquo;{saveOverwriteTarget}&rdquo;? Your current Data Extractor values
              will replace this snapshot.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={saveCancelRef} onClick={closeSaveConfirm}>
                Cancel
              </Button>
              <Button colorScheme="purple" onClick={onConfirmSaveSnapshot} ml={3}>
                Overwrite
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}

export default FinancialSnapshotData;
